import { ApolloCache, DocumentNode, gql, type Reference } from '@apollo/client';

import { NEW_GENERIC_ORDER } from './fragments/generic-order';
import { NEW_CAMPAIGN } from './fragments/campaign';
import { NEW_COMMUNICATION } from './fragments/communications';
import { NEW_ACCIDENT } from './fragments/accident';
import { NEW_ACTION } from './fragments/action';
import { NEW_ORDER } from './fragments/orders';
import { NEW_VEHICLE } from './fragments/vehicles';
import { NEW_AUX_MACHINERY } from './fragments/machinery';
import { SURVEILLANCE_ROUTE_FRAGMENT } from './fragments/surveillanceRoute';

import { SaveSurveillanceRouteInput } from '../types/surveillanceRoute';
import { markSurveillanceRouteListStale } from './surveillanceRouteListFreshness';

/**
 * Helpers reutilizables para mantener la cache de Apollo coherente tras una
 * mutación `Save<Entity>` (Bloque C). Encapsulan EXACTAMENTE el `cache.modify`
 * que cada `saveData.ts` hacía en su `update()` callback inline, de forma que:
 *
 * - El flujo online (saveData) los invoca directamente — comportamiento idéntico
 *   al inline anterior, modulo el dispatch de Redux setCursor que vive aparte
 *   en el saveData (es side effect del flujo online, no parte del cache).
 *
 * - El flujo offline (`syncOfflineQueue` / `syncCompoundQueue`) los invoca
 *   a través de {@link applyOperationCacheUpdate} para reproducir la misma
 *   actualización tras el replay. Sin esto la lista cached quedaba stale
 *   porque el callback `update(cache)` no se serializa a IndexedDB. Ver
 *   {@link applyOperationCacheUpdate} para detalles.
 *
 * **Importante (preservación de paginación)**: estos helpers SOLO hacen
 * `cache.modify` (prepend a la lista cached, increment totalCount, reset
 * `pageInfo: {}`). NO disparan `dispatch(setCursor(...))` para no mover al
 * usuario a la página 1 al sincronizar. Los saveData online sí hacen ese
 * dispatch aparte como UX deliberada de "ver lo recién creado arriba".
 */

// Tipo permisivo común: cualquier entidad de dominio se acepta mientras traiga
// `id`. Los fragments NEW_<Entity> hacen el strict-typing al persistir en cache.
type CacheEntity = { id: string; [key: string]: unknown };

// Shape de un field de Apollo cache paginado (orders, communications, …).
type PaginatedField = {
  [key: string]: unknown;
  totalCount?: number;
  pageInfo?: unknown;
};

// Apollo refs almacenados internamente como `{ __ref: "TypeName:id" }`.
type CacheRef = { __ref?: string } | null;

interface PrependOpts {
  cache: ApolloCache<unknown>;
  newEntity: CacheEntity | null | undefined;
  fieldName: string;
  itemsKey: string;
  fragment: DocumentNode;
}

function prependEntityToCacheList(opts: PrependOpts): void {
  const { cache, newEntity, fieldName, itemsKey, fragment } = opts;
  if (!newEntity?.id) return;

  cache.modify({
    fields: {
      [fieldName](
        rawValue: PaginatedField | Reference = {},
        { storeFieldName },
      ) {
        // Apollo tipa el value como `T | Reference`. Las listas paginadas
        // (`orders`, `communications`, …) son objetos inline con totalCount/
        // pageInfo, no entities normalizadas — pero por defensividad
        // devolvemos un Reference tal cual si Apollo lo pasase.
        if ('__ref' in rawValue) return rawValue;
        // «Solo marcadas» (spec 2026-10-22 § 8.3): lo recién creado NUNCA
        // cumple ese filtro (no se puede marcar lo que el servidor aún no
        // tiene), así que esa lista y su `totalCount` no se tocan — antes la
        // fila nueva salía arriba sin bandera hasta Buscar (revisión I2).
        if (storeFieldName.includes('"onlyFlagged":true')) return rawValue;
        const variables = rawValue;
        const newRef = cache.writeFragment({ data: newEntity, fragment });
        const existing =
          (variables[itemsKey] as Array<unknown> | undefined) ?? [];
        const filtered = (existing as Array<CacheRef>).filter(
          (item) => item !== null
        ) as Array<{ __ref?: string }>;

        // Idempotencia: si la entidad ya está en la lista (por __ref), no
        // duplicar. Evita doble inserción si el sync se ejecuta dos veces.
        const newRefAny = newRef as CacheRef;
        const alreadyIn = filtered.some(
          (ref) =>
            ref?.__ref && newRefAny?.__ref && ref.__ref === newRefAny.__ref
        );
        if (alreadyIn) return variables;

        return {
          ...variables,
          [itemsKey]: [newRef, ...filtered],
          totalCount: (variables.totalCount ?? 0) + 1,
          pageInfo: {},
        };
      },
    },
  });
}

/**
 * Listados con «Solo marcadas» en sus `keyArgs` (src/gql/policies.ts). Un
 * `cache.modify` por nombre de campo recorre TODAS sus entradas, también las
 * de `@connection` (cajón propio de los modales).
 */
const ONLY_FLAGGED_LISTING_FIELDS = [
  'genericOrders',
  'orders',
  'campaigns',
  'actions',
  'accidents',
  'communications',
  'surveillanceRoutes',
] as const;

/**
 * Quita de la caché las entradas de los listados filtrados por «Solo marcadas»
 * (spec 2026-10-22 § 8.3, decisión 11 del controlador). Se llama cada vez que
 * cambia el conjunto de marcas del usuario.
 *
 * Por qué: con la entrada ya cacheada, Buscar/refrescar (`cursor: ''`) va por
 * el camino del polling de `mergeCache` (MERGE_DEDUP), que conserva lo que el
 * servidor ya no devuelve — y con este filtro que una orden SALGA del conjunto
 * es el caso principal (medido en beta: el servidor devolvía 1 y la rejilla
 * seguía con 2). `mergeCache` no se toca porque lo comparte el polling; sin la
 * entrada, la siguiente respuesta entra por REPLACE_ALL.
 *
 * La fila desmarcada sigue a la vista hasta la siguiente búsqueda o refresco
 * (P6): las filas salen del loader, no de una consulta reactiva.
 */
export function evictOnlyFlaggedListings(cache: ApolloCache<unknown>): void {
  const dropOnlyFlagged = (
    value: unknown,
    { storeFieldName, DELETE }: { storeFieldName: string; DELETE: unknown },
  ) => (storeFieldName.includes('"onlyFlagged":true') ? DELETE : value);

  cache.modify({
    id: 'ROOT_QUERY',
    fields: Object.fromEntries(
      ONLY_FLAGGED_LISTING_FIELDS.map((field) => [field, dropOnlyFlagged]),
    ),
  });
}

/**
 * Añade el id de la nueva Order child al `childExpeditionOrderIDs` del parent.
 * Idempotente: si el id ya está, no se añade.
 */
function pushChildToParentOrder(
  cache: ApolloCache<unknown>,
  parentOrderId: string | undefined,
  childOrder: { id: string } | null | undefined
): void {
  if (!parentOrderId || !childOrder?.id) return;

  cache.modify({
    id: `Order:${parentOrderId}`,
    fields: {
      childExpeditionOrderIDs(variables = [], { readField }) {
        const newOrderRef = cache.writeFragment({
          data: childOrder,
          fragment: NEW_ORDER,
        });
        const newId = readField('id', newOrderRef) as string | undefined;
        const existing = variables as string[];
        if (!newId || existing.includes(newId)) return existing;
        return [...existing, newId];
      },
    },
  });
}

/**
 * Invalida queries de `orders(... isMultimedia: true)` para que la siguiente
 * lectura las refetchee — coherente con NewAction y AddVehicle inline.
 */
function invalidateMultimediaOrdersQueries(cache: ApolloCache<unknown>): void {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      orders(existing, { storeFieldName }) {
        if (storeFieldName.includes('"isMultimedia":true')) {
          return undefined;
        }
        return existing;
      },
    },
  });
}

// ─── Helpers públicos: 1 por entidad doble ────────────────────────────────

export function prependGenericOrderToCache(
  cache: ApolloCache<unknown>,
  newGenericOrder: CacheEntity | null | undefined
): void {
  prependEntityToCacheList({
    cache,
    newEntity: newGenericOrder,
    fieldName: 'genericOrders',
    itemsKey: 'genericOrders',
    fragment: NEW_GENERIC_ORDER,
  });
}

export function prependCampaignToCache(
  cache: ApolloCache<unknown>,
  newCampaign: CacheEntity | null | undefined
): void {
  prependEntityToCacheList({
    cache,
    newEntity: newCampaign,
    fieldName: 'campaigns',
    itemsKey: 'campaigns',
    fragment: NEW_CAMPAIGN,
  });
}

export function prependCommunicationToCache(
  cache: ApolloCache<unknown>,
  saveResult:
    | { communication?: CacheEntity; order?: { id: string } }
    | null
    | undefined,
  parentOrderId?: string
): void {
  if (!saveResult) return;
  prependEntityToCacheList({
    cache,
    newEntity: saveResult.communication,
    fieldName: 'communications',
    itemsKey: 'communications',
    fragment: NEW_COMMUNICATION,
  });
  pushChildToParentOrder(cache, parentOrderId, saveResult.order);
}

export function prependAccidentToCache(
  cache: ApolloCache<unknown>,
  newAccident: CacheEntity | null | undefined
): void {
  prependEntityToCacheList({
    cache,
    newEntity: newAccident,
    fieldName: 'accidents',
    itemsKey: 'accidents',
    fragment: NEW_ACCIDENT,
  });
  invalidateMultimediaOrdersQueries(cache);
}

export function prependActionToCache(
  cache: ApolloCache<unknown>,
  saveResult:
    | { action?: CacheEntity; order?: { id: string } }
    | null
    | undefined,
  parentOrderId?: string
): void {
  if (!saveResult) return;
  prependEntityToCacheList({
    cache,
    newEntity: saveResult.action,
    fieldName: 'actions',
    itemsKey: 'actions',
    fragment: NEW_ACTION,
  });
  pushChildToParentOrder(cache, parentOrderId, saveResult.order);
  invalidateMultimediaOrdersQueries(cache);
}

/**
 * Prepend del RV recién creado a la lista cacheada `surveillanceRoutes`
 * (BUG lista RV stale, 2026-07-31). El RV se había quedado FUERA de este patrón
 * canónico: su `saveData` no llamaba a ningún prepend y `applyOperationCacheUpdate`
 * no tenía su caso → como el loader del listado es cache-read-first (lee la lista
 * de caché y NO refetchea si existe), tras crear el 1er RV (caché fría → fetch)
 * los siguientes NO aparecían al volver a la tabla. Espejo exacto de
 * `prependGenericOrderToCache`. El listado lee `SURVEILLANCE_ROUTE_FRAGMENT`
 * (base); la mutación devuelve Full (⊇ base) así que el objeto trae todos los
 * campos que el fragment base persiste.
 */
export function prependSurveillanceRouteToCache(
  cache: ApolloCache<unknown>,
  newRoute: CacheEntity | null | undefined
): void {
  prependEntityToCacheList({
    cache,
    newEntity: newRoute,
    fieldName: 'surveillanceRoutes',
    itemsKey: 'surveillanceRoutes',
    fragment: SURVEILLANCE_ROUTE_FRAGMENT,
  });
}

/**
 * Invalida los caches DERIVADOS (listas paginadas + agregado del informe Gestión
 * Horaria) tras guardar/validar/cerrar/cambiar-estado una actuación u orden.
 *
 * Sus valores (horas trabajadas, estado por OT, totales) dependen de la entidad
 * pero NO se reescriben por id, y todos los loaders son cache-first → sin esto,
 * al navegar de vuelta se sirve dato VIEJO hasta pulsar "Actualizar".
 *
 * `laborHoursReport` (agregado del informe, suma de minutos por operario/empresa)
 * NO se invalidaba en ningún flujo → era la causa raíz del informe stale.
 *
 * 🔴 El recorrido de vigilancia NO entra en el array de borrado: se marca como
 * caducado (`markSurveillanceRouteListStale`). El motivo es de campo — borrar
 * su lista deja al operario con una tabla VACÍA si pierde la cobertura antes de
 * volver a ella, y ahí es donde trabaja. Detalle completo, medido, en
 * `surveillanceRouteListFreshness.ts`. Las otras seis listas se siguen
 * borrando: son de escritorio y su comportamiento no se toca aquí.
 */
export function evictDerivedCaches(cache: ApolloCache<unknown>): void {
  [
    'laborHoursReport',
    'genericOrders',
    'actions',
    'orders',
    'accidents',
    'communications',
    'campaigns',
  ].forEach((fieldName) => cache.evict({ fieldName }));
  cache.gc();
  // El RV va por marcador, no por borrado (ver arriba). Se dispara desde aquí
  // para que los llamadores que ya existían — `ChangeStateModal`,
  // `useCloseOrderConfirmation`, `TransferSubOrderModal`— refresquen también su
  // tabla sin tener que tocarlos: esos flujos SÍ cambian el estado de un RV.
  markSurveillanceRouteListStale();
}

/**
 * Tras partir, deshacer, transferir o corregir un tramo (plan 2026-10-08 D1).
 * Cambian las horas del turno, las fechas y el ROID del tramo, y las básicas
 * que se sueltan: sin esto Gestión Horaria (cache-first, caché de 3 h que
 * sobrevive a F5) y los listados enseñaban lo de antes hasta «Actualizar».
 *
 * `orderIds`: las Orders que la operación tocó y que alguna pantalla puede
 * tener normalizadas — la gemela del tramo y las básicas soltadas. Se sacan
 * de la caché para que quien las lea vaya al servidor.
 *
 * Solo se llama con la mutación ya aplicada en el servidor: partir, deshacer
 * y transferir no entran en la cola offline, y «Corregir», que sí puede
 * encolarse, lo repite al reproducirse (`applyOperationCacheUpdate`).
 */
export function evictAfterSegmentEdit(
  cache: ApolloCache<unknown>,
  orderIds: Array<string | null | undefined>
): void {
  orderIds
    .filter((id): id is string => Boolean(id))
    .forEach((id) =>
      cache.evict({ id: cache.identify({ __typename: 'Order', id }) })
    );
  evictDerivedCaches(cache);
}

/**
 * Invalida TODAS las variantes cacheadas del catálogo de plantillas de recorrido.
 *
 * 🔴 No es cosmético, y se descubrió probándolo en el navegador. Gestión lee el
 * catálogo completo (`onlyActive: false`) y el asistente solo las activas
 * (`onlyActive: true`): para Apollo son **dos entradas de caché distintas**
 * —`surveillanceRoutePresets({"onlyActive":false})` y `…({"onlyActive":true})`—,
 * así que refrescar una deja la otra intacta.
 *
 * El efecto era que una plantilla recién creada NO aparecía en el asistente: su
 * entrada se había cacheado vacía al arrancar la aplicación y, siendo
 * `cache-first`, se seguía sirviendo así. Evictar por `fieldName` se lleva
 * todas las variantes de golpe, que es justo lo que hace falta.
 *
 * Es la misma clase de trampa que ya mordió con los feature flags:
 * Administración escribía con una query y la aplicación leía con otra.
 *
 * ⚠️ Los mocks E2E responden por NOMBRE DE OPERACIÓN e ignoran las variables,
 * así que este fallo es invisible para la suite — de ahí el aviso aquí.
 */
export function evictSurveillanceRoutePresets(
  cache: ApolloCache<unknown>
): void {
  cache.evict({ fieldName: 'surveillanceRoutePresets' });
  cache.gc();
}

// ─── Helpers para listas globales tenant (vehicles, auxMachines) ─────────
//
// A diferencia de las listas de entidades dobles, `vehicles` y `auxMachines`
// son arrays directos (no objetos paginados con `totalCount`/`pageInfo`). Son
// dropdowns de selección dentro del wizard de Action, no tablas paginadas.
// El helper hace **append** (no prepend) preservando orden de creación.

interface AppendOpts {
  cache: ApolloCache<unknown>;
  newEntity: CacheEntity | null | undefined;
  fieldName: string;
  fragment: DocumentNode;
}

function appendEntityToCacheArray(opts: AppendOpts): void {
  const { cache, newEntity, fieldName, fragment } = opts;
  if (!newEntity?.id) return;

  cache.modify({
    fields: {
      [fieldName](rawValue: unknown[] | Reference = []) {
        // Apollo tipa el value como `T | Reference`. `vehicles`/`auxMachines`
        // son arrays de refs, nunca el array entero como Reference; pero por
        // defensividad devolvemos tal cual si Apollo lo pasase.
        if (!Array.isArray(rawValue)) return rawValue;
        const newRef = cache.writeFragment({ data: newEntity, fragment });
        const existingArr = rawValue as Array<CacheRef>;
        const newRefAny = newRef as CacheRef;
        const alreadyIn = existingArr.some(
          (ref) =>
            ref?.__ref && newRefAny?.__ref && ref.__ref === newRefAny.__ref
        );
        if (alreadyIn) return rawValue;
        return [...existingArr, newRef];
      },
    },
  });
}

export function appendVehicleToCache(
  cache: ApolloCache<unknown>,
  newVehicle: CacheEntity | null | undefined
): void {
  appendEntityToCacheArray({
    cache,
    newEntity: newVehicle,
    fieldName: 'vehicles',
    fragment: NEW_VEHICLE,
  });
}

export function appendAuxMachineToCache(
  cache: ApolloCache<unknown>,
  newAuxMachine: CacheEntity | null | undefined
): void {
  appendEntityToCacheArray({
    cache,
    newEntity: newAuxMachine,
    fieldName: 'auxMachines',
    fragment: NEW_AUX_MACHINERY,
  });
}

// ─── Escritura optimista offline: SAVE_GENERIC_ORDER/SAVE_CAMPAIGN (update) ──
//
// El flujo online del update-path (`input.id` presente) no necesita ningún
// helper: Apollo escribe `GenericOrder:<id>`/`Campaign:<id>`/`Order:<id>` en
// cache automáticamente al recibir la respuesta real de la mutation (ver
// comentario de `applyOperationCacheUpdate` más abajo). Pero cuando la
// mutation se encola offline (`OfflineQueueLink`), NUNCA llega esa respuesta
// — el `catch` del action solo ve un error con flag `offlineQueued` — así que
// la vista de detalle (`WorkOrderDetailed`/`CampaignDetailed`) seguía
// mostrando los valores VIEJOS hasta el sync (cazado en pruebas manuales I3
// 2026-07-04). Este helper reproduce a mano esa misma escritura.
//
// Campos Tipo B (duplicados Order↔Entity, ver fragments `NewOrder`/
// `NewGenericOrder`/`NewCampaign`) se escriben a AMBOS lados — igual que hace
// el backend en su `$transaction` (tx.order.update + tx.entity.update).
// `description`/`notes` son exclusivos de Order; `track`/`margin`/`direction`/
// `lane` son exclusivos de la Entity — no existen en el fragment contrario.
interface OfflineOptimisticEntityInput {
  id?: string; // id de la Entity (GenericOrder/Campaign) — `modelTypeId`
  registerInitDate?: string;
  pkInitKm?: number;
  pkInitMeter?: number;
  pkEndKm?: number;
  pkEndMeter?: number;
  concessionId?: string;
  roadId?: string;
  teamIds?: string[];
  capitolId?: string;
  subCapitolId?: string;
  classification?: string;
  predefinedOrderId?: string;
  description?: string;
  notes?: string;
  track?: string;
  margin?: string;
  direction?: string;
  lane?: string;
}

export function writeOfflineOptimisticOrderEntityUpdate(
  cache: ApolloCache<unknown>,
  input: OfflineOptimisticEntityInput,
  isCampaign: boolean,
  orderId: string | null | undefined
): void {
  const entityId = input.id;
  if (!entityId) return;

  const entityTypename = isCampaign ? 'Campaign' : 'GenericOrder';

  cache.modify({
    id: `${entityTypename}:${entityId}`,
    fields: {
      ...(input.registerInitDate !== undefined && {
        registerInitDate: () => input.registerInitDate,
      }),
      ...(input.pkInitKm !== undefined && { pkInitKm: () => input.pkInitKm }),
      ...(input.pkInitMeter !== undefined && {
        pkInitMeter: () => input.pkInitMeter,
      }),
      ...(input.pkEndKm !== undefined && { pkEndKm: () => input.pkEndKm }),
      ...(input.pkEndMeter !== undefined && {
        pkEndMeter: () => input.pkEndMeter,
      }),
      ...(input.teamIds !== undefined && { teamIDs: () => input.teamIds }),
      ...(input.concessionId !== undefined && {
        concessionIDs: () => [input.concessionId],
      }),
      ...(input.roadId !== undefined && { roadIDs: () => [input.roadId] }),
      ...(input.capitolId !== undefined && {
        capitolIDs: () => [input.capitolId],
      }),
      ...(input.subCapitolId !== undefined && {
        subCapitolIDs: () => [input.subCapitolId],
      }),
      ...(input.classification !== undefined && {
        classificationIDs: () => [input.classification],
      }),
      ...(input.predefinedOrderId !== undefined && {
        predefinedOrderIDs: () => [input.predefinedOrderId],
      }),
      ...(input.track !== undefined && { track: () => input.track }),
      ...(input.margin !== undefined && { margin: () => input.margin }),
      ...(input.direction !== undefined && {
        direction: () => input.direction,
      }),
      ...(input.lane !== undefined && { lane: () => input.lane }),
    },
  });

  if (!orderId) return;

  cache.modify({
    id: `Order:${orderId}`,
    fields: {
      ...(input.registerInitDate !== undefined && {
        registerInitDate: () => input.registerInitDate,
      }),
      ...(input.pkInitKm !== undefined && { pkInitKm: () => input.pkInitKm }),
      ...(input.pkInitMeter !== undefined && {
        pkInitMeter: () => input.pkInitMeter,
      }),
      ...(input.pkEndKm !== undefined && { pkEndKm: () => input.pkEndKm }),
      ...(input.pkEndMeter !== undefined && {
        pkEndMeter: () => input.pkEndMeter,
      }),
      ...(input.teamIds !== undefined && { teamIDs: () => input.teamIds }),
      ...(input.concessionId !== undefined && {
        concessionIDs: () => [input.concessionId],
      }),
      ...(input.roadId !== undefined && { roadIDs: () => [input.roadId] }),
      ...(input.capitolId !== undefined && {
        capitolIDs: () => [input.capitolId],
      }),
      ...(input.subCapitolId !== undefined && {
        subCapitolIDs: () => [input.subCapitolId],
      }),
      ...(input.classification !== undefined && {
        classificationIDs: () => [input.classification],
      }),
      ...(input.predefinedOrderId !== undefined && {
        predefinedOrderIDs: () => [input.predefinedOrderId],
      }),
      ...(input.description !== undefined && {
        description: () => input.description,
      }),
      ...(input.notes !== undefined && { notes: () => input.notes }),
    },
  });
}

// ─── Escritura optimista offline: SAVE_SURVEILLANCE_ROUTE (update) ──────────
//
// Hermana de `writeOfflineOptimisticOrderEntityUpdate` (misma doctrina).
// Disparador ORIGINAL (F2 2026-07-17): la card de la Order gemela del RV en
// la tab "Órdenes asociadas" — `ChildOrdersPage/action.ts`, retirado en D3
// (2026-07-20) al pasar esa card a read-only total (el lápiz navega al
// wizard EditRoute en vez de submitear inline). Sin caller hoy — el wizard
// (`EditRoute/action.ts`) todavía no hace escritura optimista offline en
// `saveSurveillanceRoute`; se deja este helper listo para ese caller futuro
// en vez de borrarlo. La Entity paralela es `SurveillanceRoute`, con un
// contrato de campos (`SaveSurveillanceRouteInput`,
// `types/surveillanceRoute.ts:166-183`) y un fragment
// (`SurveillanceRouteFullFragment`) distintos: sin `track`/`margin`/
// `direction`/`lane`/`capitolId`/`subCapitolId`/`predefinedOrderId` (esos son
// exclusivos de GenericOrder/Campaign), con `name`/`userIDs`/
// `vehicleOccupantIDs` exclusivos de esta Entity. No se parametriza el helper
// genérico de arriba porque el shape de campos difiere lo suficiente (haría
// el `isCampaign: boolean` insuficiente y forzaría un tercer discriminante)
// — función hermana con la misma forma en vez de forzar el reuso.
//
// Campos Tipo B (duplicados SurveillanceRoute↔Order): PKs, `teamIds`,
// `concessionId`/`roadId` → AMBAS mitades, igual que el backend en su
// `$transaction` (tx.order.update + tx.surveillanceRoute.update). `name`/
// `userIDs`/`vehicleOccupantIDs` solo existen en la Entity SurveillanceRoute
// (no en el fragment `NewOrder`). `description`/`notes`/`openingDate` son
// exclusivos de Order (no existen en `SurveillanceRouteFullFragment`).
// `vehicleAssignments` no se escribe: es un campo solo-creación (el backend
// lo consume para crear `VehicleOccupant` dentro de la `$transaction`), no
// existe como campo leíble en ningún fragment.
//
// 🔴 Hasta 2026-09-23 esta función NO LA LLAMABA NADIE, y además solo sabía
// escribir el `roadId` singular de antes de multi-carretera. Consecuencia: editar
// el recorrido sin cobertura se encolaba bien pero la tablet no veía el cambio
// hasta sincronizar — añadir la vía de apoyo no servía de nada justo donde hace
// falta. Ahora la llama `submitSurveillanceRouteEdit` (asistente EditRoute y el
// botón «Añadir al recorrido» del aviso del detector) y escribe la LISTA de
// carreteras y sus rangos.
export function writeOfflineOptimisticSurveillanceRouteUpdate(
  cache: ApolloCache<unknown>,
  input: SaveSurveillanceRouteInput,
  orderId: string | null | undefined
): void {
  const entityId = input.id;
  if (!entityId) return;

  cache.modify({
    id: `SurveillanceRoute:${entityId}`,
    fields: {
      ...(input.name !== undefined && { name: () => input.name }),
      ...(input.pkInitKm !== undefined && { pkInitKm: () => input.pkInitKm }),
      ...(input.pkInitMeter !== undefined && {
        pkInitMeter: () => input.pkInitMeter,
      }),
      ...(input.pkEndKm !== undefined && { pkEndKm: () => input.pkEndKm }),
      ...(input.pkEndMeter !== undefined && {
        pkEndMeter: () => input.pkEndMeter,
      }),
      ...(input.teamIds !== undefined && { teamIDs: () => input.teamIds }),
      ...(input.concessionId !== undefined && {
        concessionIDs: () => [input.concessionId],
      }),
      ...roadFieldsForOptimisticWrite(input),
      ...(input.userIDs !== undefined && { userIDs: () => input.userIDs }),
      ...(input.vehicleOccupantIDs !== undefined && {
        vehicleOccupantIDs: () => input.vehicleOccupantIDs,
      }),
    },
  });

  if (!orderId) return;

  cache.modify({
    id: `Order:${orderId}`,
    fields: {
      ...(input.pkInitKm !== undefined && { pkInitKm: () => input.pkInitKm }),
      ...(input.pkInitMeter !== undefined && {
        pkInitMeter: () => input.pkInitMeter,
      }),
      ...(input.pkEndKm !== undefined && { pkEndKm: () => input.pkEndKm }),
      ...(input.pkEndMeter !== undefined && {
        pkEndMeter: () => input.pkEndMeter,
      }),
      ...(input.teamIds !== undefined && { teamIDs: () => input.teamIds }),
      ...(input.concessionId !== undefined && {
        concessionIDs: () => [input.concessionId],
      }),
      // La Order no tiene `roadRanges`: solo la lista de carreteras.
      ...(roadIdsFromInput(input) !== undefined && {
        roadIDs: () => roadIdsFromInput(input),
      }),
      ...(input.description !== undefined && {
        description: () => input.description,
      }),
      ...(input.notes !== undefined && { notes: () => input.notes }),
      ...(input.openingDate !== undefined && {
        openingDate: () => input.openingDate,
      }),
    },
  });
}

/**
 * Las carreteras que trae una edición, en su forma de ahora (`roadIds`) o en la
 * de antes de multi-carretera (`roadId`). `undefined` = la edición no las toca.
 */
function roadIdsFromInput(
  input: SaveSurveillanceRouteInput
): string[] | undefined {
  if (input.roadIds !== undefined) return input.roadIds;
  if (input.roadId !== undefined) return [input.roadId];
  return undefined;
}

/**
 * `roadIDs` y `roadRanges` de la entidad, como los dejará el servidor.
 *
 * El backend guarda los rangos pegados a la lista de carreteras: si cambia la
 * lista y no llegan rangos, los vacía (`resolveRoadRanges`). Se imita aquí para
 * que la tablet no enseñe un rango que al sincronizar desaparecerá.
 *
 * Cada rango lleva su `__typename`, que es la forma en que lo trae el servidor:
 * así lo escrito a mano no se distingue de una respuesta real. (Medido con una
 * `InMemoryCache`: sin él la lectura también funciona; no es lo que la sostiene.)
 */
function roadFieldsForOptimisticWrite(
  input: SaveSurveillanceRouteInput
): Record<string, () => unknown> {
  const roadIds = roadIdsFromInput(input);
  const fields: Record<string, () => unknown> = {};
  if (roadIds !== undefined) fields.roadIDs = () => roadIds;
  if (input.roadRanges !== undefined || roadIds !== undefined) {
    const ranges = (input.roadRanges ?? []).filter(
      (range) => roadIds === undefined || roadIds.includes(range.roadId)
    );
    fields.roadRanges = () =>
      // Con una sola carretera el rango vive en los PK del recorrido y el
      // servidor guarda la lista vacía.
      roadIds !== undefined && roadIds.length <= 1
        ? []
        : ranges.map((range) => ({ __typename: 'RouteRoadRange', ...range }));
  }
  return fields;
}

/**
 * Añade vías a `SurveillanceRoute:<id>.roadIDs` en la caché, sin duplicar
 * (2026-09-23).
 *
 * La vía de APOYO —la de otro tramo donde la patrulla abre o cierra un tramo—
 * la añade el servidor al procesar el tramo. Esto adelanta lo mismo en la
 * tablet para que el cronómetro (selector de carretera, PK por GPS, detector de
 * sentido) la trate como del recorrido desde ese momento, y no tras el sondeo
 * de 30 s o, sin cobertura, tras sincronizar. Al final de la lista, que es
 * donde la conecta el servidor.
 */
export function addRoadsToSurveillanceRouteCache(
  cache: ApolloCache<unknown>,
  routeId: string,
  roadIds: string[]
): void {
  if (roadIds.length === 0) return;
  cache.modify({
    id: cache.identify({ __typename: 'SurveillanceRoute', id: routeId }),
    fields: {
      roadIDs: (existing) => {
        // Una lista de ids, nunca una referencia: si no lo fuera, no se toca.
        if (existing !== undefined && !Array.isArray(existing)) return existing;
        const currentIds: readonly string[] = existing ?? [];
        const addedIds = [...new Set(roadIds)].filter(
          (id) => !currentIds.includes(id)
        );
        return addedIds.length === 0
          ? currentIds
          : [...currentIds, ...addedIds];
      },
    },
  });
}

const ROUTE_LAST_SEGMENT_END = gql`
  fragment RouteLastSegmentEnd on SurveillanceRoute {
    id
    lastSegmentEndDate
  }
`;

/**
 * Revisión W5 (M7): el fin del último tramo del recorrido, en la caché de la
 * tablet, en cuanto se cierra un tramo — con red o encolado.
 *
 * La pregunta «¿sigues el turno…?» del cronómetro (P1) lee
 * `lastSegmentEndDate` de la caché. Sin esto, un cierre que se queda en la
 * cola no lo movía, y una tablet que llevaba más de 6 h trabajando sin
 * cobertura y reiniciaba la app recibía la pregunta sobre su PROPIO turno.
 *
 * Solo hacia adelante (un cierre viejo que llegue tarde no lo retrasa) y solo
 * si el recorrido ya está en la caché: no se fabrica una entidad a medias.
 */
export function stampRouteLastSegmentEnd(
  cache: ApolloCache<unknown>,
  routeId: string,
  endDate: string
): void {
  const id = cache.identify({ __typename: 'SurveillanceRoute', id: routeId });
  if (!id || Number.isNaN(new Date(endDate).getTime())) return;
  let current: { lastSegmentEndDate?: string | null } | null = null;
  try {
    current = cache.readFragment<{ lastSegmentEndDate?: string | null }>({
      id,
      fragment: ROUTE_LAST_SEGMENT_END,
    });
  } catch {
    current = null;
  }
  if (!current) return;
  const previous = current.lastSegmentEndDate
    ? new Date(current.lastSegmentEndDate).getTime()
    : Number.NEGATIVE_INFINITY;
  if (new Date(endDate).getTime() <= previous) return;
  cache.writeFragment({
    id,
    fragment: ROUTE_LAST_SEGMENT_END,
    data: {
      __typename: 'SurveillanceRoute',
      id: routeId,
      lastSegmentEndDate: endDate,
    },
  });
}

// ─── Espejo Order → entidad gemela: state (cambio de estado) ──────────────
//
// `UPDATE_ORDER` devuelve SOLO la Order. El backend sincroniza `Order.state`
// → la entidad horizontal (Communication/Accident/Action/GenericOrder/
// Campaign) en SERVIDOR (`OrderStateSyncService`), pero esa respuesta nunca
// viaja de vuelta al cliente — así que Apollo normaliza `Order:<id>` con el
// estado nuevo y deja `Communication:<id>` (o su hermana) con el estado
// VIEJO en caché.
//
// Mientras nadie lea el estado de la ENTIDAD esto es inofensivo (la mayoría
// de selectores usan `order.state`). Pero
// `gql/selectors/communications.ts::selectCommunicationOrderByIdOrder` hace
// `state: communication.state || order.state` — lee la ENTIDAD primero — y
// el loader del detalle es `cache-first`. Resultado (bug reportado): cambiar
// el estado desde el detalle de una Comunicación deja el chip mostrando el
// estado viejo hasta salir a la tabla y volver a entrar (el fetch de LISTA
// sí trae `state` fresco de red y reescribe la entidad de paso).
//
// Este helper reproduce en el cliente el mismo espejo que hace el backend en
// servidor, para que la caché no diverja entre la Order y su entidad gemela.
// Llamarlo tras CUALQUIER `UPDATE_ORDER` que cambie `state` con éxito — hoy
// tiene 2 callers: el de `ChangeStateModal` (orden principal; las hijas ya
// no pasan por aquí — el backend las cascadea en su propia transacción y el
// modal se limita a evictarlas de la caché) y
// `useCloseOrderConfirmation::handleSetPendingAT`. `cache.modify` sobre un
// id que no está en caché es un no-op — no hace falta comprobar existencia
// antes de llamar.
export function syncOrderStateToTwinEntityCache(
  cache: ApolloCache<unknown>,
  order:
    | {
        state?: string | null;
        communicationIDs?: string[] | null;
        accidentIDs?: string[] | null;
        actionIDs?: string[] | null;
        genericOrdersIDs?: string[] | null;
        campaignIDs?: string[] | null;
      }
    | null
    | undefined
): void {
  if (!order?.state) return;
  const { state } = order;

  const twinIdsByTypename: Array<[string, string[] | null | undefined]> = [
    ['Communication', order.communicationIDs],
    ['Accident', order.accidentIDs],
    ['Action', order.actionIDs],
    ['GenericOrder', order.genericOrdersIDs],
    ['Campaign', order.campaignIDs],
  ];

  for (const [typename, ids] of twinIdsByTypename) {
    if (!ids?.length) continue;
    for (const id of ids) {
      cache.modify({
        id: `${typename}:${id}`,
        fields: {
          state: () => state,
        },
      });
    }
  }
}

// ─── Despachador para flujo offline (sync replay) ─────────────────────────

// Shape del `data` que devuelve Apollo `mutate()` para cada Save<Entity>.
// `applyOperationCacheUpdate` acepta `unknown` en su firma pública (el caller
// pasa `result?.data` sin tipo) y narrow-cast internamente a este shape.
interface CacheUpdateData {
  saveGenericOrder?: { genericOrder?: CacheEntity };
  saveCampaign?: { campaign?: CacheEntity };
  saveCommunication?: { communication?: CacheEntity; order?: { id: string } };
  saveAccident?: { accident?: CacheEntity };
  saveAction?: { action?: CacheEntity; order?: { id: string } };
  saveSurveillanceRoute?: { surveillanceRoute?: CacheEntity };
  createVehicle?: CacheEntity;
  createAuxMachine?: CacheEntity;
  correctRouteSegment?: { orderIDs?: string[] | null };
}

/**
 * Llamado desde `syncOfflineQueue` y `syncCompoundQueue` tras cada `mutate`
 * exitoso. Reproduce el `cache.modify` que el `saveData.ts` haría en su
 * `update()` inline. Skip si la mutation es update path (`input.id`
 * presente): en ese caso Apollo ya actualiza la entidad por id automáticamente
 * (write-from-response) y no hay lista a la que añadir.
 *
 * @param cache       el ApolloClient.cache
 * @param operationName  nombre de la mutation (`SaveGenericOrder`, `SaveCampaign`, …)
 * @param variables   `{ input }` enviado al mutate
 * @param data        respuesta del backend (typed `data` del Apollo result)
 */
export function applyOperationCacheUpdate(
  cache: ApolloCache<unknown>,
  operationName: string | undefined,
  variables: Record<string, unknown> | undefined,
  data: unknown
): void {
  if (!operationName || !data || typeof data !== 'object') return;
  const d = data as CacheUpdateData;

  // Un «Corregir» de tramo encolado (sin red o por plazo vencido) cambia horas
  // y fechas del turno al reproducirse: lo mismo que el camino online
  // (plan 2026-10-08). Va antes del guard del update path porque su input
  // siempre lleva `id` (el del tramo).
  if (operationName === 'CorrectRouteSegment') {
    evictAfterSegmentEdit(cache, d.correctRouteSegment?.orderIDs ?? []);
    return;
  }

  const input = (
    variables as { input?: { id?: string; parentOrderId?: string } } | undefined
  )?.input;
  // Update path: Apollo escribe la entidad por id automáticamente al recibir
  // la respuesta. No hay nada nuevo que añadir a las listas.
  if (input?.id) return;

  switch (operationName) {
    case 'SaveGenericOrder':
      prependGenericOrderToCache(cache, d.saveGenericOrder?.genericOrder);
      break;
    case 'SaveCampaign':
      prependCampaignToCache(cache, d.saveCampaign?.campaign);
      break;
    case 'SaveCommunication':
      prependCommunicationToCache(
        cache,
        d.saveCommunication,
        input?.parentOrderId
      );
      break;
    case 'SaveAccident':
      prependAccidentToCache(cache, d.saveAccident?.accident);
      break;
    case 'SaveAction':
      prependActionToCache(cache, d.saveAction, input?.parentOrderId);
      break;
    case 'SaveSurveillanceRoute':
      // Solo el create-path (sin input.id — el guard de arriba ya salió si es
      // update). Prepend del RV a la lista cacheada al reproducir la cola
      // offline, para que el listado lo muestre sin reload al sincronizar.
      prependSurveillanceRouteToCache(
        cache,
        d.saveSurveillanceRoute?.surveillanceRoute
      );
      // Paridad con el camino online de NewRoute/saveData: el cronómetro lee
      // `activeSurveillanceRoutes` (otro campo, con sondeo de 30 s); sin el
      // evict, el recorrido recién sincronizado tardaba medio minuto en aparecer.
      cache.evict({ fieldName: 'activeSurveillanceRoutes' });
      cache.gc();
      break;
    // ─── Sub-entidades creadas dentro del wizard de Action (C-11.3) ──────
    // No son Save<Entity> Bloque C, pero el sync offline necesita actualizar
    // sus listas cached al replay para que los dropdowns muestren el item
    // recién creado sin requerir reload.
    case 'CreateVehicle':
      appendVehicleToCache(cache, d.createVehicle);
      break;
    case 'CreateAuxMachine':
      appendAuxMachineToCache(cache, d.createAuxMachine);
      break;
    default:
      // Otras mutations (DELETE_, UPDATE_, etc.) no tocan listas de entidades
      // dobles; Apollo gestiona sus writes por id automáticamente.
      break;
  }
}
